import { useMutation } from '@tanstack/react-query';

import {
  buildPartnerEarnIdempotencyKey,
  isDuplicatePartnerEarnCredit,
} from '~/components/Loyalty/PartnerEarn/partnerEarnCredit';

import { type AdminPartnerCreditBody, type AdminPartnerCreditResponse, creditPartnerEarn } from '~/services/LuxLoyalty';

/** A credit outcome plus the facts the result screen needs to report it honestly. */
export interface CreditPartnerEarnResult extends AdminPartnerCreditResponse {
  idempotencyKey: string;
  /** The service already held this exact credit (409); nothing new was queued. */
  alreadyCredited: boolean;
}

interface CreditPartnerEarnParams {
  partnerId: string;
  credit: Omit<AdminPartnerCreditBody, 'idempotencyKey'>;
}

interface UseCreditPartnerEarnOptions {
  onSuccess?: (data: CreditPartnerEarnResult) => void;
  onError?: (error: Error) => void;
}

export function useCreditPartnerEarn(options: UseCreditPartnerEarnOptions = {}) {
  return useMutation({
    mutationFn: async ({ partnerId, credit }: CreditPartnerEarnParams): Promise<CreditPartnerEarnResult> => {
      const idempotencyKey = buildPartnerEarnIdempotencyKey(partnerId, credit);
      try {
        const response = await creditPartnerEarn(partnerId, { ...credit, idempotencyKey });
        return { ...response, idempotencyKey, alreadyCredited: false };
      } catch (error) {
        // A 409 means the service already holds this exact credit. Reported separately rather than
        // as a fresh credit: the request was accepted, but no new points were queued.
        if (isDuplicatePartnerEarnCredit(error)) {
          return {
            status: 202,
            message: 'Already credited',
            transactionId: '',
            accountNumber: credit.accountNumber,
            idempotencyKey,
            alreadyCredited: true,
          };
        }
        throw error;
      }
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}
